import React, { useCallback, useEffect, useState } from 'react';
import { Gauge, Loader2, Check, FlaskConical } from 'lucide-react';
import { toast } from 'sonner';
import { CommanServices } from '@/services/commanServices';

const WEIGHT_LABELS = {
  severity: 'Severity', proximity: 'Proximity', assets: 'Assets',
  population: 'Population', corroboration: 'Independent evidence',
  history: 'History', freshness: 'Freshness',
};

// Tenant-tunable risk engine configuration: factor weights (= max
// contribution per factor) and the escalation confidence gate, with a
// dry-run preview against the most recent assessed warning so admins see
// the effect BEFORE saving.
const RiskEngineSettingsCard = () => {
  const [config, setConfig] = useState(null);
  const [weights, setWeights] = useState({});
  const [minConf, setMinConf] = useState(60);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    const res = await CommanServices.getRiskConfig();
    const cfg = (res?.data || res)?.config;
    if (cfg) {
      setConfig(cfg);
      setWeights(cfg.weights || {});
      setMinConf(cfg.escalation_min_confidence ?? 60);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalWeight = Object.values(weights).reduce((s, v) => s + (Number(v) || 0), 0);

  const runPreview = async () => {
    if (previewing) return;
    setPreviewing(true);
    setPreview(null);
    try {
      const warningsRes = await CommanServices.getEarlyWarnings(50);
      const warnings = (warningsRes?.data || warningsRes)?.warnings || [];
      const assessed = warnings.find((w) => w.risk?.assessment_id);
      if (!assessed) { toast.info('Assess a warning first — preview needs one to score against'); return; }
      const res = await CommanServices.previewRiskConfig({
        warning_id: assessed.id,
        config: { weights, escalation_min_confidence: Number(minConf) },
      });
      const data = res?.data || res;
      if (res?.status === false || !data?.preview) { toast.error(res?.message || 'Preview failed'); return; }
      setPreview({ ...data, title: assessed.title });
    } finally {
      setPreviewing(false);
    }
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await CommanServices.updateRiskConfig({
        config: { weights, escalation_min_confidence: Number(minConf) },
      });
      if (res?.status === false) { toast.error(res?.message || 'Save failed'); return; }
      toast.success('Risk engine configuration saved');
      setPreview(null);
      load();
    } finally {
      setSaving(false);
    }
  };

  if (!config) return null;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 mb-4" data-testid="risk-engine-settings-card">
      <p className="text-sm font-bold text-gray-800 flex items-center gap-1.5 mb-1">
        <Gauge className="h-4 w-4 text-violet-600" /> Risk Engine Settings
        <span className="text-[11px] font-semibold text-gray-400">config v{config.version}</span>
      </p>
      <p className="text-[11px] text-gray-500 mb-3">
        Factor weights are each factor's maximum contribution to the 0–100 score. Changes apply to NEW assessments
        only — existing versions are immutable. Every save is audited.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        {Object.keys(WEIGHT_LABELS).map((key) => (
          <label key={key} className="text-[11px] font-semibold text-gray-600">
            {WEIGHT_LABELS[key]}
            <input type="number" min="0" max="100" value={weights[key] ?? ''}
              data-testid={`risk-weight-${key}`}
              onChange={(e) => setWeights({ ...weights, [key]: e.target.value === '' ? 0 : Number(e.target.value) })}
              className="mt-0.5 w-full text-sm border border-gray-300 rounded-md bg-white px-2 py-1.5" />
          </label>
        ))}
        <label className="text-[11px] font-semibold text-gray-600">
          Escalation min confidence
          <input type="number" min="0" max="100" value={minConf}
            data-testid="risk-min-confidence"
            onChange={(e) => setMinConf(e.target.value === '' ? 0 : Number(e.target.value))}
            className="mt-0.5 w-full text-sm border border-gray-300 rounded-md bg-white px-2 py-1.5" />
        </label>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[11px] font-semibold ${totalWeight === 100 ? 'text-gray-400' : 'text-amber-600'}`}>
          Total weight: {totalWeight}{totalWeight !== 100 ? ' (score scale is 0–total)' : ''}
        </span>
        <span className="ml-auto flex items-center gap-2">
          <button onClick={runPreview} disabled={previewing} data-testid="risk-config-preview"
            className="flex items-center gap-1 rounded-md border border-violet-200 bg-white text-violet-700 text-[11px] font-bold px-2.5 py-1.5 hover:bg-violet-50 disabled:opacity-60">
            {previewing ? <Loader2 className="h-3 w-3 animate-spin" /> : <FlaskConical className="h-3 w-3" />} Preview effect
          </button>
          <button onClick={save} disabled={saving} data-testid="risk-config-save"
            className="flex items-center gap-1 rounded-md bg-violet-600 hover:bg-violet-700 text-white text-[11px] font-bold px-2.5 py-1.5 disabled:opacity-60">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Save config
          </button>
        </span>
      </div>

      {preview && (
        <div className="mt-3 rounded-lg border border-violet-100 bg-violet-50/40 px-3 py-2 text-[12px] text-gray-700" data-testid="risk-config-preview-result">
          <p className="font-semibold text-gray-800 mb-0.5">Dry-run on “{preview.title}”</p>
          <p>
            Current: {preview.current.score} · {preview.current.level} (confidence {preview.current.confidence})
            <span className="mx-1.5 text-gray-400">→</span>
            With these settings: <b>{preview.preview.score} · {preview.preview.level}</b> (confidence {preview.preview.confidence})
            {preview.preview.recommendation !== preview.current.recommendation && (
              <span className="text-violet-700"> · recommendation changes to {preview.preview.recommendation.replaceAll('_', ' ')}</span>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default RiskEngineSettingsCard;
